import type { TDestination } from './api';

export type TDestinationHealthFilter = TDestination['state'];
export type TDestinationUsedFilter = 'yes' | 'no';

export interface IDestinationFilters {
  health?: TDestinationHealthFilter;
  used?: TDestinationUsedFilter;
  hasProblems?: boolean;
  q?: string;
  cursor?: string;
}

const HEALTH: readonly string[] = [
  'healthy',
  'degraded',
  'failing',
  'idle',
  'disconnected',
  'not_counted',
  'unused',
];
const USED: readonly string[] = ['yes', 'no'];

export const parseDestinationFilters = (search: Record<string, unknown>): IDestinationFilters => {
  const text = (key: string) =>
    typeof search[key] === 'string' && search[key] !== '' ? (search[key] as string) : undefined;
  const health = text('health');
  const used = text('used');
  return {
    health: health && HEALTH.includes(health) ? (health as TDestinationHealthFilter) : undefined,
    used: used && USED.includes(used) ? (used as TDestinationUsedFilter) : undefined,
    hasProblems: search.hasProblems === true || search.hasProblems === 'true' ? true : undefined,
    q: text('q'),
    cursor: text('cursor'),
  };
};

// `true`, not `'true'`: the router JSON-encodes a string that reads as JSON.
export const destinationFiltersToSearch = (
  filters: IDestinationFilters,
): Record<string, string | boolean> => {
  const out: Record<string, string | boolean> = {};
  if (filters.health) out.health = filters.health;
  if (filters.used) out.used = filters.used;
  if (filters.hasProblems) out.hasProblems = true;
  if (filters.q) out.q = filters.q;
  if (filters.cursor) out.cursor = filters.cursor;
  return out;
};

export const destinationHasProblems = (destination: TDestination): boolean =>
  destination.state === 'failing' ||
  destination.state === 'degraded' ||
  destination.state === 'disconnected';

export const keepsDestination = (
  destination: TDestination,
  filters: IDestinationFilters,
): boolean =>
  (!filters.health || destination.state === filters.health) &&
  // `unused` is the server's own "no live flow delivers here".
  (!filters.used || (destination.state !== 'unused') === (filters.used === 'yes')) &&
  (!filters.hasProblems || destinationHasProblems(destination));
